import {
  AccessTime,
  ArrowForwardOutlined,
  CheckCircleOutline,
  ExpandLess,
  ExpandMore,
  StopCircleOutlined,
} from '@mui/icons-material'
import { Alert } from '@mui/material'
import { isEmpty } from 'lodash'
import { memo, useState } from 'react'
import { MicroLearningDetailResponse } from '../../../api/models'
import styles from '../MicroLearningDetail.module.css'
import ExperimentsTable from './ExperimentsTable'
import { MicroLearningHistoryList } from './MicroLearningHistoryCard'

export const DETAIL_TAB_CONFIG = [
  { value: 'summary', label: 'Summary' },
  { value: 'findings', label: 'Key Findings' },
  { value: 'recommendations', label: 'Recommendations' },
  { value: 'experiments', label: 'Experiments' },
  { value: 'scale-potential', label: 'Scale Potential' },
] as const

export type DetailTabValue = (typeof DETAIL_TAB_CONFIG)[number]['value']

type DetailTabsContentProps = {
  activeTab: DetailTabValue
  data: MicroLearningDetailResponse | null
  notProcessed: boolean
  historyRefreshSignal?: number
}

type EmptyStateAlertProps = {
  message: string
  showIcon?: boolean
}

const EmptyStateAlert = ({ message, showIcon = true }: EmptyStateAlertProps) => (
  <Alert severity='warning' icon={showIcon ? undefined : false} className={styles.warningBanner}>
    {message}
  </Alert>
)

const DetailTabsContent = ({ activeTab, data, notProcessed, historyRefreshSignal = 0 }: DetailTabsContentProps) => {
  const [showExperiments, setShowExperiments] = useState(true)
  const [showScalePotential, setShowScalePotential] = useState(true)
  const warningByTab = {
    summary: 'No information generated due to missing experiment data',
    findings: 'No key findings generated due to missing experiment data.',
    recommendations: {
      recommended_actions: 'No Recommended Actions generated due to missing experiment data.',
      data_gaps: 'No Data Gaps generated due to missing experiment data.',
    }
  }
  const emptyMessagesByTab = {
    summary: 'No summary or limitations available. Try regenerating',
    findings: 'No key findings available. Try regenerating',
    recommendations: {
      recommended_actions: 'No recommended actions available. Try regenerating',
      data_gaps: 'No data gaps identified',
    },
  }
  const summaryText = data?.generation?.summary?.trim()
  const limitationsText = data?.generation?.limitations?.trim()

  return (
    <div className={styles.tabPanel}>

      {activeTab === 'summary' && (
        <>
          {notProcessed ? (
            <EmptyStateAlert message={warningByTab[activeTab]} />
          ) : isEmpty(summaryText) && isEmpty(limitationsText) ? (
            <EmptyStateAlert message='No summary or limitations identified' showIcon={false} />
          ) : (
            <div className={styles.summarySectionStack}>
              {summaryText && (
                <section className={styles.summaryContentSection}>
                  <div className={styles.sectionHeaderRow}>
                    <AccessTime className={styles.sectionHeaderIcon} />
                    <h3 className={styles.sectionTitle}>SUMMARY</h3>
                  </div>
                  <p className={styles.sectionBody}>{summaryText}</p>
                </section>
              )}
              {limitationsText && (
                <section className={styles.summaryContentSection}>
                  <div className={styles.sectionHeaderRow}>
                    <StopCircleOutlined className={styles.sectionHeaderIcon} />
                    <h3 className={styles.sectionTitle}>LIMITATIONS</h3>
                  </div>
                  <p className={styles.sectionBody}>{limitationsText}</p>
                </section>
              )}
            </div>
          )}
        </>
      )}

      {activeTab === 'findings' && (
        <div className={styles.sectionStack}>
          <section className={styles.findingsContentSection}>
            <h3 className={styles.sectionTitle}>KEY FINDINGS</h3>
            {notProcessed ? (
              <EmptyStateAlert message={warningByTab[activeTab]} />
            ) : isEmpty(data?.generation?.key_findings) ? (
              <EmptyStateAlert message={emptyMessagesByTab[activeTab]} showIcon={false} />
            ) : (
              <ul className={styles.findingsList}>
                {(data?.generation?.key_findings || []).map((item, idx) => (
                  <li key={`${idx}-${item}`} className={styles.findingItem}>
                    <CheckCircleOutline className={styles.findingIcon} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}

      {activeTab === 'recommendations' && (
        <div className={styles.sectionStack}>
          <section className={styles.recommendationsContentSection}>
            <h3 className={styles.sectionTitle}>RECOMMENDED ACTIONS</h3>
            <ul className={styles.arrowList}>
              {notProcessed ? (
                <EmptyStateAlert message={warningByTab[activeTab].recommended_actions} />
              ) : isEmpty(data?.generation?.recommended_actions) ? (
                <EmptyStateAlert message={emptyMessagesByTab[activeTab].recommended_actions} showIcon={false} />
              ) : (
                (data?.generation?.recommended_actions || []).map((item, idx) => (
                  <li key={`${idx}-${item}`} className={styles.arrowListItem}>
                    <ArrowForwardOutlined className={styles.arrowListIcon} />
                    <span>{item}</span>
                  </li>
                ))
              )}
            </ul>
          </section>
          <section className={styles.recommendationsContentSection}>
            <h3 className={styles.sectionTitle}>DATA GAPS</h3>

            <ul className={styles.arrowList}>
              {notProcessed ? (
                <EmptyStateAlert message={warningByTab[activeTab].data_gaps} />
              ) : isEmpty(data?.generation?.data_gaps) ? (
                <EmptyStateAlert message={emptyMessagesByTab[activeTab].data_gaps} showIcon={false} />
              ) : (
                (data?.generation?.data_gaps || []).map((item, idx) => (
                  <li key={`${idx}-${item}`} className={styles.arrowListItem}>
                    <ArrowForwardOutlined className={styles.arrowListIcon} />
                    <span>{item}</span>
                  </li>
                ))
              )}
            </ul>

          </section>
        </div>
      )}

      {activeTab === 'experiments' && (
        <div className={styles.sectionStack}>
          <section className={styles.experimentsContentSection}>
            <button
              type='button'
              className={`${styles.experimentsSectionHeader} ${!showExperiments ? styles.experimentsSectionHeaderCollapsed : ''}`}
              onClick={() => setShowExperiments(previous => !previous)}
              aria-expanded={showExperiments}
              aria-label={showExperiments ? 'Hide experiments' : 'Show experiments'}>
              {showExperiments ? (
                <ExpandLess className={styles.experimentsSectionToggleIcon} />
              ) : (
                <ExpandMore className={styles.experimentsSectionToggleIcon} />
              )}
              <span className={styles.experimentsSectionToggleLabel}>
                {showExperiments ? 'Hide Experiments' : 'Show Experiments'}
              </span>
            </button>
            {showExperiments && <ExperimentsTable items={data?.experiments || []} showStatus={false} />}
          </section>
        </div>
      )}

      {activeTab === 'scale-potential' && (
        <div className={styles.sectionStack}>
          <section className={styles.experimentsContentSection}>
            <button
              type='button'
              className={`${styles.experimentsSectionHeader} ${!showScalePotential ? styles.experimentsSectionHeaderCollapsed : ''}`}
              onClick={() => setShowScalePotential(previous => !previous)}
              aria-expanded={showScalePotential}
              aria-label={showScalePotential ? 'Hide scale potential' : 'Show scale potential'}>
              {showScalePotential ? (
                <ExpandLess className={styles.experimentsSectionToggleIcon} />
              ) : (
                <ExpandMore className={styles.experimentsSectionToggleIcon} />
              )}
              <span className={styles.experimentsSectionToggleLabel}>
                {showScalePotential ? 'Hide Experiments' : 'Show Experiments'}
              </span>
            </button>
            {showScalePotential && <ExperimentsTable items={data?.experiments_scale_potential || []} showStatus={true} />}
          </section>
        </div>
      )}

      <MicroLearningHistoryList mlId={data?.ml_id} refreshSignal={historyRefreshSignal} />
    </div>
  )
}

export default memo(DetailTabsContent)
